import { useState, useEffect } from 'react'
import { Box, DialogContent } from '@mui/material'
import { PropTypes } from 'prop-types'
import { useTranslation } from 'react-i18next'

import DialogLayout from '../../../components/shared/dialog/dialog-layout/dialog-layout.component'
import { useDialog } from '../../../components/shared/dialog/dialog.hooks'
import {
  CLOSE_TYPES,
  SIZES,
} from '../../../components/shared/dialog/dialog-layout/dialog-layout.constants'
import syncIcon from '@hapana/shared/web/styles/assets/svgs/sync-small.svg'
import CropImage from '../../../components/shared/crop-image/crop-image.component'
import { imageToUrl } from '@hapana/shared/utils/file.utils'
import { useToast } from '../../../components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '../../../components/shared/toast/toast.constants'

import ModalUploadPhotoSuccess from './modal-upload-photo-success.component'
import ModalUploadPhoto from './modal-upload-photo.component'
import { cropStyles } from './modal-upload-photo.styles'

const ModalCropClientPhoto = ({
  image,
  originalFile,
  mutation,
  directOpen,
  isBannerImage,
  imageDelayedToLoad,
  maxImageSize,
  usePopDialog = false,
  title,
  buttonLabel,
  ...dialogProps
}) => {
  const { pushDialog, filterDialogStack, dialogState, popDialog, closeDialog } = useDialog()
  // Access setState directly to manipulate dialog stack while preserving form state
  const { t } = useTranslation('lib')
  const { openToast } = useToast()
  const [cropper, setCropper] = useState()
  const imageUrl = imageToUrl(image)
  const [isUploading, setIsUploading] = useState(false)
  const [imageDimensions, setImageDimensions] = useState(null)
  const { mutate, isLoading } = mutation
  // Check image dimensions when image loads
  useEffect(() => {
    if (imageUrl && maxImageSize) {
      const img = new Image()
      img.onload = () => {
        const dimensions = { width: img.width, height: img.height }
        setImageDimensions(dimensions)
        if (dimensions.width > maxImageSize.width || dimensions.height > maxImageSize.height) {
          openToast({
            title: t('errors:imageTooLarge'),
            description: t('errors:imageMustBeAtMost', {
              width: maxImageSize.width,
              height: maxImageSize.height,
            }),
            severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
          })
          if (usePopDialog) {
            popDialog()
          } else {
            closeDialog()
          }
        }
      }
      img.onerror = () => {
        console.error('[ModalCropPhoto] Failed to load image for dimension check')
      }
      img.src = imageUrl
    }
  }, [imageUrl, maxImageSize, openToast, t, popDialog, closeDialog, usePopDialog])

  return (
    <DialogLayout
      title={title || t('lib:profilePicture.cropAndSave')}
      closeType={CLOSE_TYPES.TEXT}
      size={SIZES.XS}
      closeAction={() => {
        if (dialogState?.dialogFilterParams?.key) {
          filterDialogStack(
            dialogState?.dialogFilterParams?.key,
            dialogState?.dialogFilterParams?.value
          )
        } else {
          if (usePopDialog) {
            popDialog()
          } else {
            closeDialog()
          }
        }
      }}
      hasMobileBottomSheet
      primaryAction={{
        label: buttonLabel || t(`lib:profilePicture.${directOpen ? 'saveChanges' : 'savePhoto'}`),
        action: async () => {
          if (!cropper?.blob) {
            console.error('No cropped image available for upload')
            return
          }

          if (maxImageSize) {
            if (!imageDimensions) {
              openToast({
                title: t('errors:imageTooLarge'),
                description: t('errors:imageValidationError'),
                severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
              })
              return
            }
            if (
              imageDimensions.width > maxImageSize.width ||
              imageDimensions.height > maxImageSize.height
            ) {
              openToast({
                title: t('errors:imageTooLarge'),
                description: t('errors:imageMustBeAtMost', {
                  width: maxImageSize.width,
                  height: maxImageSize.height,
                }),
                severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
              })
              return
            }
          }

          setIsUploading(true)
          try {
            const croppedFile = new File([cropper.blob], originalFile.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })

            const fileExtension = 'jpeg'
            const fileSize = croppedFile.size

            mutate(
              {
                corporateId: mutation.additionalData?.id,
                fileExtension,
                fileSize,
                file: croppedFile,
              },
              {
                onSuccess: () => {
                  mutation?.onUploadSuccess?.(cropper)
                  if (!imageDelayedToLoad) {
                    if (dialogState?.skipSuccessDialog) {
                      if (directOpen || usePopDialog) {
                        closeDialog()
                      }
                    } else {
                      if (directOpen || usePopDialog) {
                        closeDialog()
                      }
                      pushDialog(ModalUploadPhotoSuccess, {
                        image: cropper.url,
                        imageDelayedToLoad,
                      })
                    }
                  }

                  setIsUploading(false)
                },
                onError: (error) => {
                  console.error('Upload failed:', error)
                  openToast({
                    title: t('errors:api.generic'),
                    severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
                  })
                  setIsUploading(false)
                },
              }
            )
          } catch (error) {
            console.error('Upload failed:', error)
            openToast({
              title: t('errors:api.generic'),
              severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
            })
            setIsUploading(false)
          }
        },
        isLoading: isLoading || isUploading,
        props: {
          disabled: isUploading || isLoading,
        },
      }}
      secondaryAction={
        !directOpen && {
          label: t('lib:profilePicture.startOver'),
          action: () =>
            pushDialog(ModalUploadPhoto, {
              mutation,
              ...dialogProps,
              isBannerImage,
            }),
          props: {
            startIcon: syncIcon,
          },
        }
      }
    >
      <DialogContent sx={cropStyles.dialogContent}>
        <Box sx={cropStyles.cropperContainer}>
          <CropImage
            cropperWrapperStyles={cropStyles.cropperWrapper}
            cropperStyles={cropStyles.cropper}
            isAvatar
            setCropper={setCropper}
            image={imageUrl}
            isBannerImage={isBannerImage}
            maxImageSize={maxImageSize}
          />
        </Box>
      </DialogContent>
    </DialogLayout>
  )
}
ModalCropClientPhoto.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Blob)]).isRequired,
  originalFile: PropTypes.instanceOf(File).isRequired,
  mutation: PropTypes.shape({
    mutate: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    responseKey: PropTypes.string.isRequired,
    onUploadSuccess: PropTypes.func,
    additionalData: PropTypes.object,
  }).isRequired,
  directOpen: PropTypes.bool,
  isBannerImage: PropTypes.bool,
  imageDelayedToLoad: PropTypes.bool,
  maxImageSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  usePopDialog: PropTypes.bool,
  title: PropTypes.string,
  buttonLabel: PropTypes.string,
}

export default ModalCropClientPhoto
